import {Observable, share, Subject, tap} from "rxjs";
import {container, inject, singleton} from "tsyringe";
import {ICommandHandler} from "../commands/handlers/i-command.handler";
import {ICommand} from "../commands/i-command.interface";
import {isSensitivePayload} from "../commands/payloads/is-sensitive-payload.helper";
import {BackendClient} from "../connectivity/backend-client";
import {EventName} from "../events/event-name.enum";
import {StringHelper} from "../helpers/string.helper";
import {TransformerHelper} from "../helpers/transformer.helper";
import {MessageBroker} from "../message-broker/message-broker";
import {CommandSendPayload} from "../message-broker/payloads/command-send.payload";
import {ClassOf} from "../types/class-of.type";
import {EventBus} from "./event-bus.service";

type SendingBufferItem = {
    resolve: (value: boolean | PromiseLike<boolean>) => void,
    command: ICommand
}

@singleton()
export class CommandBus {

    private _commandPipe: Subject<ICommand> = new Subject<ICommand>();

    private _history: ICommand[] = []

    private _commandPipe$: Observable<ICommand> = this._commandPipe.pipe(
        tap(command => this._history.unshift(command)),
        share()
    );

    private _isSending = false;

    private _registeredCommandHandlers = new Map<ClassOf<ICommand>, ClassOf<ICommandHandler<any>>>();

    private _registeredCommands = new Map<ClassOf<object>, Map<string, ClassOf<ICommand>>>();

    private _sendingBuffer: Array<SendingBufferItem> = [];

    constructor(
        @inject(BackendClient) private readonly _backendClient: BackendClient,
        @inject(EventBus) private readonly _eventBus: EventBus
    ) {
        this._commandPipe$.subscribe(async command => {
            if (this._registeredCommandHandlers.has(command.constructor as ClassOf<ICommand>)) {
                const commandHandlerPrototype = this._registeredCommandHandlers.get(command.constructor as ClassOf<ICommand>)!;
                const commandHandler = container.resolve(commandHandlerPrototype);
                commandHandler.handle(command);
            } else {
                await this.addSendCommand(command);
            }
        })
    }

    public async addSendCommand(command: ICommand): Promise<boolean> {

        const promise = new Promise<boolean>(resolve => {
            this._sendingBuffer.push({
                command,
                resolve
            });
        })

        if (!this._isSending) {
            await this._sendCommands();
        }

        return promise;
    }

    public async dispatchCommand<T extends ICommand>(command: T): Promise<boolean> {
        const listener = this._eventBus.listenOnceForCommandSend(command);

        this._commandPipe.next(command);

        return listener;
    }

    /**
     * Like `dispatchCommand`, but resolves the WHOLE `COMMAND_SEND` payload
     * instead of only its `success` boolean — so a caller can tell a lost edit
     * lock (`error instanceof LockConflictError`) from a crashed server.
     *
     * A SIBLING rather than a change to `dispatchCommand`: that method has many
     * callers who all mean "did it work?", and widening its return type would
     * touch every one of them for the benefit of the few that care why.
     *
     * The ordering below is the same as its sibling's and matters for the same
     * reason: SUBSCRIBE FIRST, then push the command onto the pipe. Pushing
     * first would let a synchronously-settling send emit COMMAND_SEND before
     * anything is listening, and the await would never resolve.
     */
    public async dispatchCommandForResult<T extends ICommand>(command: T): Promise<CommandSendPayload> {
        const listener = this._eventBus.listenOnceForCommandSendResult(command);

        this._commandPipe.next(command);

        return listener;
    }

    /**
     * Whether `command`'s class has a dedicated handler registered via
     * `@CommandHandler(...)`. If it does, that handler owns publishing its own
     * `entity.*` event on success — `CommandSendListener`'s generic fallback
     * (`command.send` -> `entity.*`) is then a deliberate, safe no-op for that
     * command's `command.payload`, not a gap to warn about (review 04,
     * WARNING, Linse A, 2026-08-18: without this distinction the fallback's
     * diagnostic `console.warn` fired on EVERY successful call of a
     * dedicated-handler command with a non-Entity payload — e.g. every single
     * KnowledgeObject rename — even though nothing was actually broken).
     */
    public hasRegisteredHandler(command: ICommand): boolean {
        return this._registeredCommandHandlers.has(command.constructor as ClassOf<ICommand>);
    }

    public getCommand<T extends object>(prototype: ClassOf<T>, type: string): ClassOf<ICommand> | null {
        if (!this._isCommandRegistered(prototype, type)) {
            console.error(`[command-bus] kein Command für ${type}.${StringHelper.toKebabCase(prototype.name)} registriert.`)
            return null;
        }

        return this._registeredCommands.get(prototype)!.get(type)!;
    }

    public registerCommand<T extends object>(prototype: ClassOf<T>, type: string, commandPrototype: ClassOf<ICommand>): void {
        if (this._isCommandRegistered(prototype, type)) {
            throw new Error(`[message-broker] Es ist bereits ein Command für ${type}.${StringHelper.toKebabCase(prototype.name)} registriert.`);
        }

        this._setRegisteredCommand(prototype, type, commandPrototype);
    }

    /**
     * @param commandPrototype
     * @param commandHandlerPrototype
     */
    public registerCommandHandler<T extends any>(commandPrototype: ClassOf<ICommand>, commandHandlerPrototype: ClassOf<ICommandHandler<T>>): void {

        if (this._registeredCommandHandlers.has(commandPrototype)) {
            throw new Error(`For command "${commandPrototype.name}" is a command handler already registered`);
        }

        this._registeredCommandHandlers.set(commandPrototype, commandHandlerPrototype);
    }

    /**
     * @todo Im CommandHandler statt hier das Command bearbeiten.
     * Eigentlich sollte das Senden an das Backend im CommandHandler passieren, aber das wäre hier ein riesiger Aufwand
     * es jetzt umzustellen. Deshalb diese allgemeine Methode die alle Commands an das Backend sendet.
     */
    protected async _sendCommands(): Promise<void> {
        this._isSending = true;

        let sendingBufferItem = this._sendingBuffer.shift();
        while (!!sendingBufferItem) {
            try {
                // Inside the try since feature plan 0054 (ADR-016): a throwing toDTO used to leave the
                // loop before `_isSending = false`, so not a single further command left the browser
                // for the rest of the session. Now it settles like any other failed send.
                let payload: Awaited<ReturnType<typeof TransformerHelper.toDTO>>;
                try {
                    payload = await TransformerHelper.toDTO(sendingBufferItem.command);
                } finally {
                    // The body is built (or failed to build): no secret stays on the command from here
                    // on — not in the history, not for the listeners below (settings plan B).
                    CommandBus._clearSensitivePayloads(sendingBufferItem.command);
                }

                await this._backendClient.post<void>(
                    `/api/v1/command`,
                    false, // no authorization implemented - test
                    payload
                );
                const success = true;

                this._eventBus.dispatch<CommandSendPayload>(EventName.COMMAND_SEND, {
                    success,
                    command: sendingBufferItem.command
                });

                container.resolve(MessageBroker).publish<CommandSendPayload>({
                    type: `command.send`,
                    identityHash: sendingBufferItem.command.uid,
                    payload: {
                        command: sendingBufferItem.command,
                        success
                    }
                });
                // hier müsste die richtige message rein als reaktion auf das erfolgreiche command
                sendingBufferItem.resolve(success);
            } catch (error) {
                console.error(error);

                // Settle the caller on failure. `dispatchCommand` awaits
                // `listenOnceForCommandSend`, which resolves off the COMMAND_SEND
                // event — without dispatching it here (with success=false) that
                // await never settles and the caller hangs forever on a backend
                // error (e.g. HTTP 500) or a command whose toDTO threw (no transformer,
                // a payload class without @Expose). Also resolve the buffer promise so
                // `addSendCommand` does not leak either.
                // The error rides ALONG WITH the settle, never instead of it.
                // Both resolutions below stay exactly as they were: the dispatch
                // is what `dispatchCommand` awaits, so replacing it with a
                // re-throw would hang every caller forever — the bug the comment
                // above records. Adding `error` beside `success` lets a reader
                // that wants the distinction have it, and changes nothing for
                // every reader that does not.
                this._eventBus.dispatch<CommandSendPayload>(EventName.COMMAND_SEND, {
                    success: false,
                    command: sendingBufferItem.command,
                    error
                });
                sendingBufferItem.resolve(false);
            }

            sendingBufferItem = this._sendingBuffer.shift();
        }

        this._isSending = false;
    }

    /** Clears every SensitivePayload of `command`, and of the commands inside it when it is a bulk. */
    private static _clearSensitivePayloads(command: ICommand): void {
        const payload: unknown = command.payload;
        if (isSensitivePayload(payload)) {
            payload.clearSensitiveValues();
        } else if (Array.isArray(payload)) {
            for (const inner of payload) {
                if (typeof inner === 'object' && inner !== null && 'payload' in inner) {
                    CommandBus._clearSensitivePayloads(inner as ICommand);
                }
            }
        }
    }

    private _isCommandRegistered<T extends object>(prototype: ClassOf<T>, type: string): boolean {
        if (!this._registeredCommands.has(prototype)) {
            return false;
        }

        return this._registeredCommands.get(prototype)!.has(type);
    }

    private _setRegisteredCommand<T extends object>(prototype: ClassOf<T>, type: string, commandPrototype: ClassOf<ICommand>): void {
        if (!this._registeredCommands.has(prototype)) {
            this._registeredCommands.set(prototype, new Map<string, ClassOf<ICommand>>());
        }

        this._registeredCommands.get(prototype)!.set(type, commandPrototype);
        `[message-broker] Command «${type}.${StringHelper.toKebabCase(prototype.name)}» registriert.`
    }
}